import {
  getSegmentDetail,
  getSegmentABDetail,
  getProfileSegmentOptions,
  GetSegmentDetailRequestType,
  postCreateSegment,
  PostCreateSegmentRequestType,
  putUpdateSegment,
  PutUpdateSegmentRequestType,
  putUpdateSegmentAB,
} from 'features/Segmentation/services/segmentCreateEdit'
import { createAsyncThunk } from '@reduxjs/toolkit'
import { callErrorMsg } from 'helpers/errorMsg'
import { toastSuccess } from 'utils/toast'
import { getAllBrandV2, getBrandDetailById } from 'features/Product/services/brand'
import {
  getCategoryList,
  GetCategoryParamsType,
} from 'features/CategoryAndPosisi/services/infiniteScrollTabAddEdit'
import { getProductList } from 'features/Product/services/product'
import { getProductDetailById } from 'features/Product/services/packageStructure'
import { getCategoryById } from 'features/CategoryAndPosisi/services/category'

import {
  SLICE_NAME,
  setIsLoading,
  setSegmentDetail,
  setProfileOptions,
  setBrandOptions,
  setCategoryL1Options,
  setCategoryL2Options,
  setProductOptions,
  setIsResolvingSelectedProducts,
} from './slice'

export { type PostCreateSegmentRequestType, type PutUpdateSegmentRequestType }

const composeProductOptionName = (productName: string, grammation?: string) => {
  const suffix = grammation?.trim()
  return suffix ? `${productName} ${suffix}` : productName
}

export const fetchSegmentDetail = createAsyncThunk(
  `${SLICE_NAME}/fetchSegmentDetail`,
  async ({ segmentId }: GetSegmentDetailRequestType, { dispatch, rejectWithValue }) => {
    dispatch(setIsLoading(true))

    try {
      const { data } = await getSegmentDetail({ segmentId })

      dispatch(setSegmentDetail(data.data))
      dispatch(setIsLoading(false))

      return data
    } catch (error) {
      dispatch(setIsLoading(false))
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)

export const fetchSegmentABDetail = createAsyncThunk(
  `${SLICE_NAME}/fetchSegment`,
  async ({ segmentId }: GetSegmentDetailRequestType, { dispatch, rejectWithValue }) => {
    dispatch(setIsLoading(true))

    try {
      const { data } = await getSegmentABDetail(segmentId)

      dispatch(setSegmentDetail(data.data))
      dispatch(setIsLoading(false))

      return data
    } catch (error) {
      dispatch(setIsLoading(false))
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)

export const fetchProfileSegmentOptions = createAsyncThunk(
  `${SLICE_NAME}/fetchProfileSegmentOptions`,
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await getProfileSegmentOptions()

      dispatch(setProfileOptions(data.data.map((item) => ({ name: item.name, value: item.id }))))
      return data
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)

export const createSegment = createAsyncThunk(
  `${SLICE_NAME}/createSegment`,
  async ({ data: payload }: PostCreateSegmentRequestType, { dispatch, rejectWithValue }) => {
    dispatch(setIsLoading(true))

    try {
      const { data } = await postCreateSegment({ data: payload })
      dispatch(setIsLoading(false))
      toastSuccess(data.data.message || 'Segment created successfully')
      return data
    } catch (error) {
      dispatch(setIsLoading(false))
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)

export const updateSegment = createAsyncThunk(
  `${SLICE_NAME}/updateSegment`,
  async (
    { segmentId, data: payload }: PutUpdateSegmentRequestType,
    { dispatch, rejectWithValue },
  ) => {
    dispatch(setIsLoading(true))

    try {
      const { data } = await putUpdateSegment({ segmentId, data: payload })
      dispatch(setIsLoading(false))
      toastSuccess(data.data.message || 'Segment updated successfully')
      return data
    } catch (error) {
      dispatch(setIsLoading(false))
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)

export const updateSegmentAB = createAsyncThunk(
  `${SLICE_NAME}/updateSegmentAB`,
  async (
    { segmentId, data: payload }: PutUpdateSegmentRequestType,
    { dispatch, rejectWithValue },
  ) => {
    dispatch(setIsLoading(true))

    try {
      const { data } = await putUpdateSegmentAB({ segmentId, data: payload })
      dispatch(setIsLoading(false))
      toastSuccess(data.data.message || 'Segment updated successfully')
      return data
    } catch (error) {
      dispatch(setIsLoading(false))
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)

export const fetchBrandV2Options = createAsyncThunk(
  `${SLICE_NAME}/fetchBrandV2Options`,
  async ({ name }: { name: string }, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await getAllBrandV2({ name, pageSize: 20 })

      dispatch(
        setBrandOptions(data.data.map((item) => ({ name: item.brand_name, value: item.brand_id }))),
      )
      return data.data
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)

export type FetchCategoryOptionsParamsType = Pick<GetCategoryParamsType, 'name'> & {
  isL2?: boolean
  isL1?: boolean
}
export const fetchCategoryOptions = createAsyncThunk(
  `${SLICE_NAME}/fetchCategoryOptions`,
  async (
    { name, isL2 = false, isL1 = false }: FetchCategoryOptionsParamsType,
    { dispatch, rejectWithValue },
  ) => {
    try {
      const { data } = await getCategoryList({ name, pageSize: 20 })

      if (isL1) {
        dispatch(
          setCategoryL1Options(
            data.content.map((item) => ({ name: item.category_name, value: item.category_id })),
          ),
        )
      }
      if (isL2) {
        const childCategories = data.content
          .flatMap((item) => item.category_childs || [])
          .map((child) => ({ name: child.category_name, value: child.category_id }))
        dispatch(setCategoryL2Options(childCategories))
      }

      return data.content
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)

export const fetchProductOptions = createAsyncThunk(
  `${SLICE_NAME}/fetchProductOptions`,
  async (name: string | undefined, { rejectWithValue, dispatch }) => {
    try {
      const response = await getProductList({
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'pagination.page_index': 1,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'pagination.page_size': 20,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'filter.is_active': true,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'filter.name': name,
      })

      dispatch(
        setProductOptions(
          response.data.data.map((el) => ({
            name: composeProductOptionName(el.product_name, el.selling_unit_grammation_description),
            value: el.product_id,
          })),
        ),
      )
      return response.data
    } catch (error) {
      return rejectWithValue(error)
    }
  },
)

export const fetchBrandDetails = createAsyncThunk(
  `${SLICE_NAME}/fetchBrandDetails`,
  async ({ brandIds }: { brandIds: number[] }, { rejectWithValue }) => {
    try {
      const res = await Promise.all(brandIds.map((id) => getBrandDetailById(id)))

      return res.map((el) => ({ value: el.data.data.brand_id, name: el.data.data.brand_name }))
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)

export const fetchProductDetails = createAsyncThunk(
  `${SLICE_NAME}/fetchProductDetails`,
  async ({ brandIds }: { brandIds: number[] }, { rejectWithValue, dispatch }) => {
    dispatch(setIsResolvingSelectedProducts(true))

    try {
      const res = await Promise.all(brandIds.map((id) => getProductDetailById(id)))

      return res.map((el) => ({
        value: el.data.data.product_id,
        name: composeProductOptionName(
          el.data.data.product_name,
          el.data.data.selling_unit_grammation_description,
        ),
      }))
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    } finally {
      dispatch(setIsResolvingSelectedProducts(false))
    }
  },
)

export const fetchCategoryDetails = createAsyncThunk(
  `${SLICE_NAME}/fetchCategoryL1L2Details`,
  async ({ ids }: { ids: number[] }, { rejectWithValue }) => {
    try {
      const res = await Promise.all(ids.map((id) => getCategoryById(id)))

      return res.map((el) => ({ value: el.data.category_id, name: el.data.category_name }))
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)
